import React, { useState, useEffect, Fragment } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import useAuthStore from '../../stores/authStore';
import { useAuth } from '../../hooks/useAuth';
import {
  DashboardIcon,
  SyncIcon,
  PackageIcon,
  SettingsIcon,
  ApiIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '../icons';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  TagIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ClockIcon,
  UsersIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  children?: NavItem[];
}

interface NavItemComponentProps {
  item: NavItem;
}

const NavItemComponent: React.FC<NavItemComponentProps> = ({ item }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;

  const isChildActive = hasChildren && item.children?.some(child =>
    child.path && location.pathname.startsWith(child.path)
  );

  const [isExpanded, setIsExpanded] = useState(isChildActive || false);

  useEffect(() => {
    if (isChildActive) {
      setIsExpanded(true);
    }
  }, [isChildActive]);

  const IconComponent = item.icon;

  const activeClasses = 'bg-[color:var(--color-brand-primary)]/[0.08] text-[color:var(--color-brand-primary)] border-l-[3px] border-[color:var(--color-brand-primary)] font-semibold';
  const inactiveClasses = 'text-gray-600 hover:bg-[#EAECF0] border-l-[3px] border-transparent';

  if (hasChildren) {
    return (
      <div>
        <div className="flex items-center mx-2">
          {item.path ? (
            <NavLink
              to={item.path}
              end
              className={({ isActive }) => `
                flex-1 flex items-center px-3 py-2 text-sm rounded-md transition-colors
                ${isActive ? activeClasses : inactiveClasses}
              `}
            >
              <div className="flex items-center gap-2.5">
                <IconComponent className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{item.label}</span>
              </div>
            </NavLink>
          ) : (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex-1 flex items-center px-3 py-2 text-sm rounded-md text-left transition-colors ${inactiveClasses}`}
            >
              <div className="flex items-center gap-2.5">
                <IconComponent className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{item.label}</span>
              </div>
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-1.5 py-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-[#EAECF0]"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-0.5 ml-4 space-y-0.5">
            {item.children?.map((child) => (
              <NavItemComponent key={child.id} item={child} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path || `/${item.id}`}
      end
      className={({ isActive }) => `
        mx-2 w-auto flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors
        ${isActive ? activeClasses : inactiveClasses}
      `}
    >
      <div className="flex items-center gap-2.5">
        <IconComponent className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium">{item.label}</span>
      </div>
    </NavLink>
  );
};

const Sidebar: React.FC = () => {
  const { user } = useAuthStore();
  const { logout, getDisplayName, getInitials } = useAuth();
  const navigate = useNavigate();

  const mainNavItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
    },
    {
      id: 'products',
      label: 'Produtos',
      icon: PackageIcon,
      path: '/products',
    },
    {
      id: 'sync',
      label: 'Sincronização',
      icon: SyncIcon,
      children: [
        {
          id: 'sync-main',
          label: 'Import/Export',
          icon: SyncIcon,
          path: '/sync',
        },
        {
          id: 'sync-jobs',
          label: 'Jobs',
          icon: ClockIcon,
          path: '/sync/jobs',
        },
        {
          id: 'sync-product-history',
          label: 'Logs Produto',
          icon: ClockIcon,
          path: '/sync/product-history',
        },
      ],
    },
    {
      id: 'orders',
      label: 'Pedidos',
      icon: PackageIcon,
      path: '/orders',
    },
  ];

  const cadastrosNavItems: NavItem[] = [
    {
      id: 'cadastros',
      label: 'Cadastros',
      icon: CircleStackIcon,
      children: [
        {
          id: 'brands',
          label: 'Marcas',
          icon: TagIcon,
          path: '/brands',
        },
        ...(user?.is_staff || user?.company_role === 'owner' || user?.company_role === 'admin' ? [{
          id: 'users',
          label: 'Usuarios',
          icon: UsersIcon,
          path: '/users',
        }] : []),
      ],
    },
  ];

  const settingsNavItems: NavItem[] = [
    {
      id: 'configuration',
      label: 'Configuração',
      icon: SettingsIcon,
      children: [
        {
          id: 'platforms',
          label: 'Plataformas',
          icon: ApiIcon,
          path: '/configuration/platforms',
        },
      ],
    },
  ];

  const adminNavItems: NavItem[] = [
    {
      id: 'admin',
      label: 'Administração',
      icon: ShieldCheckIcon,
      children: [
        {
          id: 'companies',
          label: 'Empresas',
          icon: BuildingOfficeIcon,
          path: '/companies',
        },
        {
          id: 'sync-discovery',
          label: 'Discovery Monitor',
          icon: ChartBarIcon,
          path: '/sync/discovery',
        },
      ],
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  return (
    <div
      className="h-full flex flex-col border-r"
      style={{
        width: 'var(--space-sidebar-width)',
        minWidth: 'var(--space-sidebar-width)',
        backgroundColor: 'var(--color-surface-sidebar)',
        borderColor: 'var(--color-border-subtle)',
      }}
    >
      {/* Logo area — matches header height */}
      <div
        className="flex items-center px-5 border-b"
        style={{
          height: 'var(--space-header-height)',
          minHeight: 'var(--space-header-height)',
          borderColor: 'var(--color-border-subtle)',
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-1 h-5 rounded-full"
            style={{ backgroundColor: 'var(--color-brand-primary)' }}
          />
          <h1 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Hub Shoppub
          </h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-5 space-y-6 overflow-y-auto">
        {/* Main Navigation */}
        <div>
          <h3
            className="px-5 mb-2 uppercase tracking-wider font-semibold"
            style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}
          >
            Principal
          </h3>
          <div className="space-y-0.5">
            {mainNavItems.map((item) => (
              <NavItemComponent key={item.id} item={item} />
            ))}
          </div>
        </div>

        {/* Cadastros Navigation */}
        <div>
          <h3
            className="px-5 mb-2 uppercase tracking-wider font-semibold"
            style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}
          >
            Cadastros
          </h3>
          <div className="space-y-0.5">
            {cadastrosNavItems.map((item) => (
              <NavItemComponent key={item.id} item={item} />
            ))}
          </div>
        </div>

        {/* Admin Navigation - staff ve tudo; o operador (can_create_companies) so Empresas */}
        {(user?.is_staff || user?.can_create_companies) && (
          <div>
            <h3
              className="px-5 mb-2 uppercase tracking-wider font-semibold"
              style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}
            >
              Administração
            </h3>
            <div className="space-y-0.5">
              {adminNavItems.map((item) => (
                <NavItemComponent
                  key={item.id}
                  item={user?.is_staff ? item : {
                    ...item,
                    children: item.children?.filter(child => child.id === 'companies'),
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Settings Navigation */}
        <div>
          <h3
            className="px-5 mb-2 uppercase tracking-wider font-semibold"
            style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}
          >
            Outros
          </h3>
          <div className="space-y-0.5">
            {settingsNavItems.map((item) => (
              <NavItemComponent key={item.id} item={item} />
            ))}
          </div>
        </div>
      </nav>

      {/* User area */}
      <div className="border-t" style={{ borderColor: 'var(--color-border-subtle)' }}>
        <Menu as="div" className="relative">
          <Menu.Button className="w-full flex items-center gap-3 px-4 hover:bg-[#EAECF0] transition-colors" style={{ height: '64px' }}>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--color-brand-primary)' }}
            >
              <span className="text-white text-sm font-medium">
                {getInitials()}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
                {getDisplayName()}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                {user?.email || ''}
              </p>
            </div>
            <EllipsisVerticalIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute bottom-full left-2 right-2 mb-1 z-50 rounded-md bg-white shadow-dropdown ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => navigate('/profile')}
                      className={`flex items-center w-full px-4 py-2 text-sm ${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      <UserIcon className="w-4 h-4 mr-3 text-gray-400" />
                      Meu Perfil
                    </button>
                  )}
                </Menu.Item>
                <div className="border-t border-gray-100" />
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={`flex items-center w-full px-4 py-2 text-sm ${
                        active ? 'bg-red-50 text-red-900' : 'text-red-700'
                      }`}
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3 text-red-400" />
                      Sair
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
};

export default Sidebar;
